// DEPENDENCIES ---------------------------------------------------------------- //

// import React from 'react';

import { Moves } from '@gql/base-types';

import { getFormattedVehicleFromMove } from '@services/moveService';
import { resolveMoveHeroImageUrl, type MoveHeroPhoto } from '@features/moves/utils/moveHeroImage';

import { css } from '@emotion/css';
import { Theme, useTheme, Avatar, Typography, Tooltip } from '@mui/material';
import { FaCar } from 'react-icons/fa';

// TYPES ---------------------------------------------------------------- //

interface MoveDetailsVehicleImageProps {
  move?: Moves;
}

/**
 * The query-shaped fields the hero image reads. The photo aliases and the workflow
 * step trees are not on the broad `Moves` type, so the move is narrowed to just what
 * this component needs.
 */
interface MoveHeroSource {
  pickup_photos?: (MoveHeroPhoto | null)[] | null;
  delivery_photos?: (MoveHeroPhoto | null)[] | null;
  workflowset?: {
    pickupWorkflow?: { steps?: unknown } | null;
    deliveryWorkflow?: { steps?: unknown } | null;
  } | null;
}

// COMPONENT ---------------------------------------------------------------- //

const MoveDetailsVehicleImage = ({ move }: MoveDetailsVehicleImageProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const source = move as unknown as MoveHeroSource | undefined;
  const getVehicleImageUrl = () =>
    resolveMoveHeroImageUrl({
      deliveryPhotos: source?.delivery_photos,
      pickupPhotos: source?.pickup_photos,
      deliverySteps: source?.workflowset?.deliveryWorkflow?.steps,
      pickupSteps: source?.workflowset?.pickupWorkflow?.steps,
    });

  return (
    <div className={cls.image}>
      <Typography sx={{ fontWeight: 600, color: theme?.palette?.text?.disabled }}>VEHICLE</Typography>

      <div className={cls.imageBox}>
        <Avatar
          alt={getFormattedVehicleFromMove(move) || `Vehicle`}
          src={getVehicleImageUrl()}
          sx={{
            width: 96,
            height: 96,
            backgroundColor: theme?.palette?.utility?.lighter,
          }}
        >
          <FaCar size={56} />
        </Avatar>

        <Tooltip title={getFormattedVehicleFromMove(move) || `Vehicle`} placement='top'>
          <div className={cls.imageBoxShadow} />
        </Tooltip>
      </div>
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme) => ({
  image: css`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    margin-top: 6px;
    ${theme?.breakpoints?.down(`sm`)} {
      display: none;
    }
  `,
  imageBox: css`
    position: relative;
  `,
  imageBoxShadow: css`
    z-index: 1;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 50%;
    box-shadow: 0px 0px 12px 2px #00000030 inset;
    cursor: pointer;
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default MoveDetailsVehicleImage;
